import type { AxiosError } from 'axios';

import Notification from 'components/partials/notification/notification.helper';

import { UNAUTHORIZED, NOT_FOUND } from 'constants/error-types';
import { useOkta } from 'utils/okta/use-okta';
import { clearOktaSessionInfo, getOktaBearerToken } from 'helpers/okta-session';

interface ApiErrorResponseData {
  detail?: string;
  [key: string]: unknown;
}

export interface ApiErrorWithMeta extends Error {
  status?: number;
  statusText?: string;
  data?: ApiErrorResponseData;
}

const sessionExpiredHandler = (
  title = 'Session Expired',
  message = 'Your session has expired, please log in again.',
  type = 'info',
): void => {
  clearOktaSessionInfo();
  Notification.flash(title, type, message);
  window.location.href = '/login';
};

export const errorInterceptors = async (
  error: AxiosError<ApiErrorResponseData> | Record<string, never> = {} as Record<string, never>,
): Promise<never> => {
  const okta = useOkta();
  const axiosError = error as AxiosError<ApiErrorResponseData>;
  const { status, statusText, data } = axiosError?.response ?? {};

  if (!getOktaBearerToken()) {
    sessionExpiredHandler();

    const err: ApiErrorWithMeta = Object.assign(
      new Error('Unauthorized: No bearer token'),
      { status, statusText, data },
    );

    return Promise.reject(err);
  }

  if (status && status === UNAUTHORIZED) {
    await okta.signOut().then(() => {
      sessionExpiredHandler();
    });
  } else if (
    status &&
    data &&
    status === NOT_FOUND &&
    data.detail === 'Subscription not active'
  ) {
    sessionExpiredHandler(
      'Error logging in',
      `Your school's subscription is locked.`,
      'error',
    );
  } else if (axiosError.response) {
    console.error('API response error', axiosError.response);
  }

  const err: ApiErrorWithMeta = Object.assign(
    new Error(`API Error: ${statusText || 'Unknown error'}`),
    { status, statusText, data },
  );

  return Promise.reject(err);
};
